<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import analyzersApi from 'dashboard/api/analysis/analyzers';
import criteriaApi from 'dashboard/api/analysis/criteria';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AnalyzerDialog from './components/AnalyzerDialog.vue';

const { t } = useI18n();

const records = ref([]);
const criteria = ref([]);
const isLoading = ref(false);
const selected = ref(null);
const formDialogRef = ref(null);
const deleteDialogRef = ref(null);

const tableHeaders = computed(() => [
  t('ANALYSIS.ANALYZERS.TABLE.NAME'),
  t('ANALYSIS.ANALYZERS.TABLE.CRITERIA'),
  t('ANALYSIS.ANALYZERS.TABLE.TRIGGER'),
  t('ANALYSIS.ANALYZERS.TABLE.STATUS'),
  t('ANALYSIS.ANALYZERS.TABLE.ACTIONS'),
]);

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [analyzersResponse, criteriaResponse] = await Promise.all([
      analyzersApi.get(),
      criteriaApi.get({ activeOnly: true }),
    ]);
    records.value = analyzersResponse.data.payload;
    criteria.value = criteriaResponse.data.payload;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchAll);

const openCreate = () => {
  selected.value = null;
  formDialogRef.value.open();
};

const openEdit = record => {
  selected.value = record;
  formDialogRef.value.open();
};

const openDelete = record => {
  selected.value = record;
  deleteDialogRef.value.open();
};

const onSave = async payload => {
  const editing = Boolean(selected.value?.id);
  try {
    if (editing) {
      await analyzersApi.update(selected.value.id, payload);
    } else {
      await analyzersApi.create(payload);
    }
    formDialogRef.value.close();
    useAlert(
      t(
        editing
          ? 'ANALYSIS.ANALYZERS.EDIT.SUCCESS'
          : 'ANALYSIS.ANALYZERS.CREATE.SUCCESS'
      )
    );
    await fetchAll();
  } catch (error) {
    const message =
      error?.response?.data?.message ??
      t(
        editing
          ? 'ANALYSIS.ANALYZERS.EDIT.ERROR'
          : 'ANALYSIS.ANALYZERS.CREATE.ERROR'
      );
    formDialogRef.value.setError(message);
  }
};

const onDelete = async () => {
  try {
    await analyzersApi.delete(selected.value.id);
    useAlert(t('ANALYSIS.ANALYZERS.DELETE.SUCCESS'));
    await fetchAll();
  } catch {
    useAlert(t('ANALYSIS.ANALYZERS.DELETE.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="!records.length"
    :no-records-message="t('ANALYSIS.ANALYZERS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('ANALYSIS.ANALYZERS.HEADER')"
        :description="t('ANALYSIS.ANALYZERS.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            icon="i-lucide-plus"
            :label="t('ANALYSIS.ANALYZERS.NEW')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable :headers="tableHeaders" :items="records">
        <template #row="{ items }">
          <BaseTableRow v-for="record in items" :key="record.id" :item="record">
            <BaseTableCell>
              <div class="flex flex-col">
                <span class="font-medium text-n-slate-12">{{
                  record.name
                }}</span>
                <span class="text-n-slate-11 line-clamp-1">
                  {{ record.description }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell>
              <span class="text-n-slate-11">{{ record.criteria.length }}</span>
            </BaseTableCell>
            <BaseTableCell>
              {{
                t(
                  `ANALYSIS.ANALYZERS.TRIGGER.${record.trigger_type.toUpperCase()}`
                )
              }}
            </BaseTableCell>
            <BaseTableCell>
              <WootLabel
                :label="
                  record.active
                    ? t('ANALYSIS.CRITERIA.ACTIVE')
                    : t('ANALYSIS.CRITERIA.INACTIVE')
                "
                :color="record.active ? 'teal' : 'slate'"
                compact
              />
            </BaseTableCell>
            <BaseTableCell>
              <div class="flex gap-1">
                <NextButton
                  icon="i-lucide-pencil"
                  slate
                  ghost
                  xs
                  @click="openEdit(record)"
                />
                <NextButton
                  icon="i-lucide-trash-2"
                  ruby
                  ghost
                  xs
                  @click="openDelete(record)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <AnalyzerDialog
      ref="formDialogRef"
      :analyzer="selected"
      :available-criteria="criteria"
      @saved="onSave"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('ANALYSIS.ANALYZERS.DELETE.TITLE')"
      :description="t('ANALYSIS.ANALYZERS.DELETE.MESSAGE')"
      :confirm-button-label="t('ANALYSIS.ANALYZERS.DELETE.CONFIRM')"
      :cancel-button-label="t('ANALYSIS.ANALYZERS.DELETE.CANCEL')"
      @confirm="onDelete"
    />
  </SettingsLayout>
</template>
